import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from './utils';

export type RadioProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label?: ReactNode;
  hint?: ReactNode;
};

export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { label, hint, className, id, ...rest },
  ref,
) {
  const reactId = useId();
  const fieldId = id ?? `radio-${reactId}`;
  return (
    <label
      htmlFor={fieldId}
      className={cn(
        'group inline-flex items-start gap-2.5 text-ink-700 cursor-pointer select-none',
        className,
      )}
    >
      <span className="relative w-[16px] h-[16px] rounded-full border border-ink-300 grid place-items-center transition-colors group-hover:border-ink-700 overflow-hidden mt-0.5">
        <input
          ref={ref}
          id={fieldId}
          type="radio"
          {...rest}
          className="peer absolute inset-0 opacity-0 cursor-pointer"
        />
        <span className="absolute inset-0 scale-0 peer-checked:scale-100 transition-transform duration-200 ease-out bg-ink-900 rounded-full" />
        <span className="relative z-10 w-[5px] h-[5px] rounded-full bg-white scale-0 peer-checked:scale-100 transition-transform duration-200 ease-out" />
      </span>
      {(label || hint) && (
        <span className="leading-tight text-[12.5px]">
          {label && <span className="block">{label}</span>}
          {hint && <span className="block text-ink-500 text-[11.5px] mt-0.5">{hint}</span>}
        </span>
      )}
    </label>
  );
});

export interface RadioGroupProps {
  name: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: ReactNode; hint?: ReactNode; disabled?: boolean }>;
  /** Vertical (default) or horizontal layout. */
  orientation?: 'vertical' | 'horizontal';
  className?: string;
}

export function RadioGroup({
  name,
  value,
  onChange,
  options,
  orientation = 'vertical',
  className,
}: RadioGroupProps) {
  return (
    <div
      role="radiogroup"
      className={cn(
        orientation === 'vertical' ? 'flex flex-col gap-2.5' : 'flex flex-wrap gap-4',
        className,
      )}
    >
      {options.map((o) => (
        <Radio
          key={o.value}
          name={name}
          value={o.value}
          checked={value === o.value}
          disabled={o.disabled}
          onChange={() => onChange(o.value)}
          label={o.label}
          hint={o.hint}
        />
      ))}
    </div>
  );
}
